import React, { useState, useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { onInputKeyDown } from '@aytm/app/stats/page/sidebar/components/lib/utils'

import { Button } from 'primereact/button'
import { InputTextarea } from 'primereact/inputtextarea'

import style from '@aytm/app/stats/page/sidebar/coding/styles/CodingPanel.module.scss'

export const DataContext = React.createContext()

const InputNew = ({
  placeholder,
  maxLength,
  forbiddenSymbols,
  error,
  multiline,
  isAdding,
  isProcessing,
  onAdd,
  onToggle
}) => {
  const [inputValue, setInputValue] = useState('')

  const callbackRef = useRef(null)

  const onInputKeyUp = useCallback(
    event => {
      if (13 == event.keyCode) onAdd(inputValue)
      if (27 == event.keyCode) onToggle(false)
    },
    [inputValue, onAdd, onToggle]
  )

  const onInputChange = event => setInputValue(event.target.value)

  const onInputPaste = useCallback(
    event => {
      event.preventDefault()

      let text =
        (event.clipboardData || window.clipboardData).getData('Text') || event.clipboardData.getData('text/plain')
      text = text.replace(new RegExp(`[${forbiddenSymbols}]`, 'gi'), '')

      const paste = () => {
        text = text.replace(/(\r\n|\n|\r|\t|\v)/gm, ' ').trim()

        const el = event.currentTarget

        const l = text.length
        const start = el.selectionStart
        const end = el.selectionEnd

        const valueBefore = el.value.slice(0, start)
        const valueAfter = el.value.slice(end)

        text = `${valueBefore}${text}${valueAfter}`.slice(0, maxLength)

        setInputValue(text)
        callbackRef.current = () => {
          el.setSelectionRange(start + l, start + l)
          el.blur()
          el.focus()
        }
      }

      if (multiline) {
        let textLines = (text && 0 < text.length ? text.split(/(\r\n|\n|\r)/gm) : []).filter(t => 0 < t.trim().length)

        const item = textLines.shift()
        textLines = textLines.filter((v, i, a) => a.indexOf(v) == i)

        if (0 < textLines.length) {
          onAdd(item, textLines)
        } else paste()
      } else {
        paste()
      }
    },
    [maxLength, forbiddenSymbols, multiline, onAdd]
  )

  useEffect(() => {
    if (null !== callbackRef.current && 'function' == typeof callbackRef.current) {
      callbackRef.current()
      callbackRef.current = null
    }
  }, [inputValue])

  useEffect(() => !isAdding && setInputValue(''), [isAdding])

  return (
    <div className={style['component-add']}>
      {isAdding ? (
        <>
          <div className={style['component-textarea']}>
            <InputTextarea
              className="p-composite"
              placeholder={placeholder}
              value={inputValue}
              maxLength={maxLength}
              keyfilter={new RegExp(`^[^${forbiddenSymbols}]*$`, 'i')}
              rows={1}
              autoFocus
              autoResize
              disabled={isProcessing}
              onKeyDown={isProcessing ? null : onInputKeyDown}
              onKeyUp={isProcessing ? null : onInputKeyUp}
              onChange={isProcessing ? null : onInputChange}
              onPaste={isProcessing ? null : onInputPaste}
            />
            <span className={style['component-textarea-count']}>{maxLength - inputValue.length}</span>
          </div>

          {error && <div className={style['component-error']}>{error.message}</div>}

          <div className={style['component-add-buttons']}>
            <Button
              className="p-button-stat p-button-secondary"
              label="Cancel"
              disabled={isProcessing}
              onClick={isProcessing ? null : () => onToggle(false)}
            />
            <Button
              className="p-button-stat"
              label="Ok"
              loading={isProcessing}
              disabled={isProcessing}
              onClick={isProcessing ? null : () => onAdd(inputValue)}
            />
          </div>
        </>
      ) : (
        <span
          className={classNames(style['component-add-button'], {
            [style['disabled']]: isProcessing
          })}
          onClick={isProcessing ? null : () => onToggle(true)}
        >
          <i className="icon-aytmfd-add" />
          Add a {placeholder.toLowerCase()}
        </span>
      )}
    </div>
  )
}

InputNew.propTypes = {
  placeholder: PropTypes.string,
  maxLength: PropTypes.number,
  forbiddenSymbols: PropTypes.string,
  error: PropTypes.object,
  multiline: PropTypes.bool,
  isAdding: PropTypes.bool,
  isProcessing: PropTypes.bool,
  onAdd: PropTypes.func,
  onToggle: PropTypes.func
}

InputNew.displayName = 'InputNew'

export default InputNew
